import React, { useState, useEffect, useRef, useId } from 'react';
import { RiArrowDownSLine } from '@remixicon/react';
import { cn } from '../../../../components/ui/utils';

/**
 * Phone Input Component
 * Phone input with country code selector and formatting
 *
 * @param {Object} props
 * @param {string} props.value - Phone number (without country code)
 * @param {string} props.countryCode - Country code (e.g., '+1')
 * @param {Function} props.onChange - Callback when value changes (receives {phone, countryCode})
 * @param {string} props.error - Error message
 * @param {boolean} props.disabled - Whether input is disabled
 */
const PhoneInput = ({
  value = '',
  countryCode = '+1',
  onChange,
  error = '',
  disabled = false
}) => {
  // Country data with flags and dial codes
  const COUNTRIES = [
    { code: 'US', name: 'United States', dial: '+1', flag: '🇺🇸', format: '(###) ###-####', placeholder: '(555) 123-4567', length: 10 },
    { code: 'GB', name: 'United Kingdom', dial: '+44', flag: '🇬🇧', format: '### #### ####', placeholder: '020 1234 5678', length: [10, 11] },
    { code: 'CA', name: 'Canada', dial: '+1', flag: '🇨🇦', format: '(###) ###-####', placeholder: '(555) 123-4567', length: 10 },
    { code: 'AU', name: 'Australia', dial: '+61', flag: '🇦🇺', format: '### ### ###', placeholder: '412 345 678', length: 9 },
  ];

  const [selectedCountry, setSelectedCountry] = useState(
    COUNTRIES.find(c => c.dial === countryCode) || COUNTRIES[0]
  );
  const [phoneNumber, setPhoneNumber] = useState(value || '');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const errorId = useId();

  // Update selected country when countryCode prop changes
  useEffect(() => {
    const country = COUNTRIES.find(c => c.dial === countryCode);
    if (country) {
      setSelectedCountry(country);
    } else {
      // Handle unknown country codes
      setSelectedCountry({
        code: 'XX',
        name: 'Unknown',
        dial: countryCode,
        flag: '🏳️',
        format: '##########',
        placeholder: '',
        length: 15
      });
    }
  }, [countryCode]);

  // Update phone number when value prop changes
  useEffect(() => {
    let digits = (value || '').replace(/\D/g, '');
    // Strip leading country code digits if present (e.g. "15551234567" → "5551234567" for +1)
    const dialDigits = selectedCountry.dial.replace(/\D/g, '');
    const maxLen = Array.isArray(selectedCountry.length) ? selectedCountry.length[1] : selectedCountry.length;
    if (dialDigits && digits.startsWith(dialDigits) && digits.length > maxLen) {
      digits = digits.slice(dialDigits.length);
    }
    const formatted = formatPhoneNumber(digits, selectedCountry.format, selectedCountry.length);
    setPhoneNumber(formatted);
  }, [value]);

  // Format phone number based on country format
  const formatPhoneNumber = (input, format, maxLength) => {
    // Remove all non-digit characters
    const digits = input.replace(/\D/g, '');

    if (!digits) return '';

    // Check max length
    const limit = Array.isArray(maxLength) ? maxLength[1] : maxLength;
    const limitedDigits = digits.slice(0, limit);

    // Apply format pattern
    let formatted = '';
    let digitIndex = 0;

    for (let i = 0; i < format.length && digitIndex < limitedDigits.length; i++) {
      if (format[i] === '#') {
        formatted += limitedDigits[digitIndex];
        digitIndex++;
      } else {
        formatted += format[i];
      }
    }

    return formatted;
  };

  // Validate phone number length
  const validatePhoneNumber = (digits, country) => {
    const length = Array.isArray(country.length) ? country.length : [country.length];
    const digitsOnly = digits.replace(/\D/g, '');

    if (digitsOnly.length === 0) return '';

    if (!length.includes(digitsOnly.length)) {
      if (country.code === 'GB') {
        return 'Phone number must be 10-11 digits';
      } else if (country.code === 'US' || country.code === 'CA') {
        return 'Phone number must be 10 digits';
      }
      return `Phone number must be ${length.join(' or ')} digits`;
    }

    return '';
  };

  // Handle phone number input change
  const handlePhoneChange = (e) => {
    const input = e.target.value;
    const formatted = formatPhoneNumber(input, selectedCountry.format, selectedCountry.length);
    setPhoneNumber(formatted);

    // Notify parent with unformatted phone number
    const digitsOnly = input.replace(/\D/g, '');
    onChange?.({
      phone: digitsOnly,
      countryCode: selectedCountry.dial
    });
  };

  // Handle country selection
  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setIsDropdownOpen(false);
    setSearchQuery('');
    setHighlightedIndex(-1);

    // Reformat existing phone number for new country
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    const formatted = formatPhoneNumber(digitsOnly, country.format, country.length);
    setPhoneNumber(formatted);

    // Notify parent with new country code
    onChange?.({
      phone: digitsOnly,
      countryCode: country.dial
    });
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isDropdownOpen) {
      if (e.key === 'Enter') {
        e.preventDefault();
        setIsDropdownOpen(true);
      }
      return;
    }

    const filteredCountries = COUNTRIES.filter(c =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredCountries.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : filteredCountries.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredCountries.length) {
          handleCountrySelect(filteredCountries[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsDropdownOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setIsDropdownOpen(false);
        setSearchQuery('');
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Filter countries based on search
  const filteredCountries = COUNTRIES.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get validation error
  const validationError = phoneNumber ? validatePhoneNumber(phoneNumber, selectedCountry) : '';
  const displayError = error || validationError;

  // Get placeholder based on country
  const getPlaceholder = () => {
    return selectedCountry.placeholder || selectedCountry.format.replace(/#/g, '5');
  };

  return (
    <div className="relative w-full">
      {/* Combined input with country selector */}
      <div className="relative" ref={dropdownRef}>
        <div className="relative flex items-center">
          {/* Country selector button inside input */}
          <button
            type="button"
            onClick={() => !disabled && setIsDropdownOpen(!isDropdownOpen)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={isDropdownOpen}
            aria-label="Select country"
            className={cn(
              'absolute left-3 flex items-center gap-1 px-2 py-1 rounded text-sm transition-all z-10',
              'focus:outline-none focus:ring-2 focus:ring-blue-500',
              disabled && 'cursor-not-allowed opacity-60',
              !disabled && 'hover:bg-gray-50'
            )}
          >
            <span
              className="text-base leading-none"
              role="img"
              aria-label={selectedCountry.name}
              data-testid="country-flag"
              data-country={selectedCountry.code}
            >
              {selectedCountry.flag}
            </span>
            <span className="text-gray-700 font-medium text-sm">{selectedCountry.dial}</span>
            <RiArrowDownSLine
              className={cn(
                'w-3.5 h-3.5 text-gray-400 transition-transform',
                isDropdownOpen && 'transform rotate-180'
              )}
            />
          </button>

          {/* Phone number input */}
          <input
            ref={inputRef}
            type="tel"
            value={phoneNumber}
            onChange={handlePhoneChange}
            placeholder={getPlaceholder()}
            disabled={disabled}
            aria-label="Phone number"
            aria-invalid={displayError ? 'true' : 'false'}
            aria-describedby={displayError ? errorId : undefined}
            autoComplete="tel"
            className={cn(
              'w-full pl-24 pr-4 py-2.5 border rounded-lg text-sm transition-all',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
              displayError && 'error border-red-500 focus:ring-red-500',
              disabled && 'disabled bg-gray-100 cursor-not-allowed opacity-60',
              !displayError && !disabled && 'border-gray-300 hover:border-gray-400'
            )}
          />
        </div>

        {/* Country dropdown */}
        {isDropdownOpen && !disabled && (
          <div
            role="listbox"
            aria-label="Countries"
            className="absolute z-50 top-full mt-1 left-0 w-64 bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden"
          >
            {/* Search input */}
            <div className="p-2 border-b border-gray-200">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search country"
                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Country list */}
            <div className="max-h-64 overflow-y-auto">
              {filteredCountries.map((country, index) => (
                <button
                  key={country.code}
                  type="button"
                  role="option"
                  aria-selected={country.code === selectedCountry.code}
                  onClick={() => handleCountrySelect(country)}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors',
                    'hover:bg-gray-50 focus:outline-none focus:bg-gray-50',
                    country.code === selectedCountry.code && 'bg-blue-50',
                    index === highlightedIndex && 'highlighted bg-gray-100'
                  )}
                >
                  <span className="text-xl leading-none" role="img" aria-label={country.name}>
                    {country.flag}
                  </span>
                  <span className="text-sm text-gray-900">
                    {country.name} ({country.dial})
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Error message */}
      {displayError && (
        <div
          id={errorId}
          className="mt-1 text-xs text-red-600"
        >
          {displayError}
        </div>
      )}
    </div>
  );
};

export default PhoneInput;
